import React from 'react';
import { ChakraProvider, CSSReset, Flex } from '@chakra-ui/react';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import theme from './Styles/theme';
import './i18n';

import RedesignShell from './Components/general/RedesignShell';
import Navbar from './Components/general/Navbar';
import Footer from './Components/general/Footer';
import BackStageNavbar from './Components/general/BackStageNavbar';
import BackStageFooter from './Components/general/BackStageFooter';

// Redesigned pages
import RedesignHomePage from './Pages/Redesign/HomePage';
import VCIntroPage from './Pages/Redesign/VCIntroPage';
import VCApplyPage from './Pages/Redesign/VCApplyPage';
import VCHelpPage from './Pages/Redesign/VCHelpPage';
import NewsPage from './Pages/Redesign/NewsPage';
import CenterIntroPage from './Pages/Redesign/CenterIntroPage';
import ArchitecturePage from './Pages/Redesign/ArchitecturePage';
import PartnersPage from './Pages/Redesign/PartnersPage';
import RedesignFAQPage from './Pages/Redesign/FAQPage';
import ContactPage from './Pages/Redesign/ContactPage';

// Other frontstage pages
import Page404 from './Pages/Page404';
import UploadPage from './Pages/UploadPage';
import RegisterPage from './Pages/RegisterPage';
import VCVerificationPage from './Pages/VC-Verification';
import EditVCVerificationPage from './Pages/EditVCVerificationPage';
import ComingSoon from './Pages/ComingSoon';

// Backstage pages
import LoginPage from './Pages/Backstage/LoginPage';
import StatisticPage from './Pages/Backstage/StatisticPage';
import StatisticsManagerPage from './Pages/Backstage/StatisticsManagerPage';
import BulletinManagerPage from './Pages/Backstage/BulletinManagerPage';
import CreateBulletinPage from './Pages/Backstage/CreateBulletinPage';
import EditBulletinPage from './Pages/Backstage/EditBulletinPage';
import FileManagerPage from './Pages/Backstage/FileManagerPage';
import CreateFilePage from './Pages/Backstage/CreateFilePage';
import EditFilePage from './Pages/Backstage/EditFilePage';
import IssuerManagerPage from './Pages/Backstage/IssuerManagerPage';
import CreateIssuerPage from './Pages/Backstage/CreateIssuerPage';
import EditIssuerPage from './Pages/Backstage/EditIssuerPage';
import PlanManagerPage from './Pages/Backstage/PlanManagerPage';
import CreatePlanPage from './Pages/Backstage/CreatePlanPage';
import EditPlanPage from './Pages/Backstage/EditPlanPage';
import PrivateRoute from './Pages/Backstage/PrivateRoute';

import ScrollToTop from './Utils/scrollToTop';

const FrontStagePage: React.FC<{
  page: React.ComponentType<any>;
}> = ({ page: Page }) => {
  return (
    <Flex key="FrontStagePage" position="relative" w="100%" minH="100vh" direction="column">
      <Navbar />
      <Flex flex="1" direction="column" overflow="auto">
        <Page />
      </Flex>
      <Footer />
    </Flex>
  );
};

const RedesignPage: React.FC<{
  page: React.ComponentType<any>;
}> = ({ page: Page }) => {
  return (
    <RedesignShell>
      <Page />
    </RedesignShell>
  );
};

const BackStagePage: React.FC<{
  page: React.ComponentType<any>;
}> = ({ page: Page }) => {
  return (
    <Flex key="BackStagePage" position="relative" w="100%" minH="100vh" direction="column">
      <BackStageNavbar />
      <Flex flex="1" direction="column" overflow="auto">
        <Page />
      </Flex>
      <BackStageFooter />
    </Flex>
  );
};

const WithoutNavbar: React.FC<{
  page: React.ComponentType<any>;
}> = ({ page: Page }) => {
  return (
    <Flex key="WithoutNavbar" position="relative" w="100%" minH="100vh" direction="column">
      <Page />
      <BackStageFooter />
    </Flex>
  );
};

function App() {
  return (
    <ChakraProvider theme={theme}>
      <CSSReset />
      <BrowserRouter>
        <ScrollToTop />
        <Switch>
          {/* Redesigned Frontstage Routes */}
          <Route exact path="/" render={() => <RedesignPage page={RedesignHomePage} />} />

          {/* 數位憑證 (VC) */}
          <Route exact path="/vc" render={() => <RedesignPage page={VCIntroPage} />} />
          <Route exact path="/vc/intro" render={() => <RedesignPage page={VCIntroPage} />} />
          <Route exact path="/vc/apply" render={() => <RedesignPage page={VCApplyPage} />} />
          <Route exact path="/vc/help" render={() => <RedesignPage page={VCHelpPage} />} />

          {/* 最新消息 (News: 公告 + 文件下載) */}
          <Route exact path="/news" render={() => <RedesignPage page={NewsPage} />} />

          {/* 關於中心 (About) */}
          <Route exact path="/about" render={() => <RedesignPage page={CenterIntroPage} />} />
          <Route exact path="/about/center" render={() => <RedesignPage page={CenterIntroPage} />} />
          <Route exact path="/about/architecture" render={() => <RedesignPage page={ArchitecturePage} />} />
          <Route exact path="/about/partners" render={() => <RedesignPage page={PartnersPage} />} />
          <Route exact path="/about/faq" render={() => <RedesignPage page={RedesignFAQPage} />} />
          <Route exact path="/about/contact" render={() => <RedesignPage page={ContactPage} />} />

          {/* Backward compatibility redirects */}
          <Redirect exact from="/bulletin" to="/news" />
          <Redirect exact from="/document" to="/news" />
          <Redirect exact from="/FAQ" to="/about/faq" />
          <Redirect exact from="/vc-application" to="/vc/apply" />

          {/* Other Frontstage */}
          <Route exact path="/dcertupload" render={() => <FrontStagePage page={UploadPage} />} />
          <Route exact path="/vc-verification" render={() => <FrontStagePage page={VCVerificationPage} />} />
          <Route exact path="/vc-verification/register" render={() => <FrontStagePage page={RegisterPage} />} />
          <Route exact path="/vc-verification/edit" render={() => <FrontStagePage page={EditVCVerificationPage} />} />
          <Route exact path="/comingsoon" render={() => <FrontStagePage page={ComingSoon} />} />

          {/* Backstage */}
          <Route exact path="/bs/login" render={() => <WithoutNavbar page={LoginPage} />} />
          <PrivateRoute exact path="/bs/statistic" render={() => <BackStagePage page={StatisticPage} />} />
          <PrivateRoute exact path="/bs/stat-manager" render={() => <BackStagePage page={StatisticsManagerPage} />} />
          <PrivateRoute exact path="/bs/bulletin" render={() => <BackStagePage page={BulletinManagerPage} />} />
          <PrivateRoute exact path="/bs/bulletin/create" render={() => <BackStagePage page={CreateBulletinPage} />} />
          <PrivateRoute exact path="/bs/bulletin/edit" render={() => <BackStagePage page={EditBulletinPage} />} />
          <PrivateRoute exact path="/bs/document" render={() => <BackStagePage page={FileManagerPage} />} />
          <PrivateRoute exact path="/bs/document/create" render={() => <BackStagePage page={CreateFilePage} />} />
          <PrivateRoute exact path="/bs/document/edit" render={() => <BackStagePage page={EditFilePage} />} />
          <PrivateRoute exact path="/bs/issuer" render={() => <BackStagePage page={IssuerManagerPage} />} />
          <PrivateRoute exact path="/bs/issuer/create" render={() => <BackStagePage page={CreateIssuerPage} />} />
          <PrivateRoute exact path="/bs/issuer/edit" render={() => <BackStagePage page={EditIssuerPage} />} />
          <PrivateRoute exact path="/bs/plan" render={() => <BackStagePage page={PlanManagerPage} />} />
          <PrivateRoute exact path="/bs/plan/create" render={() => <BackStagePage page={CreatePlanPage} />} />
          <PrivateRoute exact path="/bs/plan/edit" render={() => <BackStagePage page={EditPlanPage} />} />

          {/* 404 */}
          <Route path="/" render={() => <FrontStagePage page={Page404} />} />
        </Switch>
      </BrowserRouter>
    </ChakraProvider>
  );
}

export default App;
